import { useEffect, useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { useQuery, useMutation } from '@tanstack/react-query';
import { dv } from '../../i18n/dv.ts';
import { formatDvDate, formatDvTime } from '../../lib/dvDate.ts';
import { yaumiyyaApi } from '../yaumiyya/api.ts';
import { meetingsApi, type Body, type MeetingDetail } from './api.ts';
import { displayName } from '../../lib/utils.ts';

// Shows (and, when editable, lets schedulers change) who the signed agenda is
// emailed to. Editable until the agenda passes; read-only afterwards.
//
// Once the agenda has passed (agendaPassedAt set) the card becomes a read-only
// "send details" receipt — sent time / method / verified-via / recipients (and,
// if the send failed, a prominent error). The sent time comes from the recorded
// send (agendaSend.sentAt), falling back to agendaPassedAt for meetings sent
// before that record existed.
export function MeetingRecipientsPanel({ meetingId, body, recipientIds, editable, agendaPassedAt, agendaSend, onSaved }: {
  meetingId: string; body: Body; recipientIds: string[]; editable: boolean;
  agendaPassedAt: string | null;
  agendaSend: MeetingDetail['agendaSend'];
  onSaved: () => void;
}): JSX.Element {
  const { data: members } = useQuery({
    queryKey: ['council-members', body],
    queryFn: () => yaumiyyaApi.councilMembers(body),
  });
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(recipientIds);
  useEffect(() => { setSelected(recipientIds); }, [recipientIds]);

  const save = useMutation({
    mutationFn: () => meetingsApi.updateRecipients(meetingId, selected),
    onSuccess: () => { setOpen(false); onSaved(); },
  });

  const byId = new Map((members ?? []).map((m) => [m.id, m]));
  const shown = recipientIds.map((id) => byId.get(id)).filter((m): m is NonNullable<typeof m> => !!m);

  // After the agenda is sent → read-only "send details" receipt. Styled to match
  // the sibling cards on the meeting detail page: card-paper surface, a
  // status-label header with a success pill, aligned label→value rows separated
  // by hairline dividers, and recipient names as neat chips.
  if (agendaPassedAt) {
    const sentIso = agendaSend?.sentAt ?? agendaPassedAt;
    const failed = agendaSend?.success === false;
    return (
      <section className="rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6 space-y-3">
        {/* Header — heading + a calm status pill, matching the meeting header. */}
        <div className="flex items-center justify-between gap-2">
          <h2 className="status-label text-muted-foreground">{dv.meetings.sentDetailsTitle}</h2>
          {failed
            ? <span className="pill pill-danger">{dv.meetings.sentPillFailed}</span>
            : <span className="pill pill-success">{dv.meetings.sentPillOk}</span>}
        </div>

        {/* Failure alert — bordered + tinted destructive block with icon, title, message. */}
        {failed && (
          <div className="flex gap-2.5 rounded-2xl bg-[hsl(var(--error-container))] p-3 text-[hsl(var(--destructive))]">
            <Icon name="warning" size={18} className="mt-0.5 shrink-0" />
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-semibold">{dv.meetings.sendErrorTitle}</p>
              {agendaSend?.error && <p className="break-words text-sm/relaxed opacity-90">{agendaSend.error}</p>}
            </div>
          </div>
        )}

        {/* Sent metadata — icon-led inline items, mirroring the meeting header's
            date/time/location row (Calendar / Clock / Mail). */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Icon name="calendar_today" size={16} className="shrink-0 text-foreground/50" />
            {formatDvDate(sentIso)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Icon name="schedule" size={16} className="shrink-0 text-foreground/50" />
            <span className="tabular" dir="ltr">{formatDvTime(sentIso)}</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Icon name="mail" size={16} className="shrink-0 text-foreground/50" />
            {dv.meetings.sentMethodEmail}
          </span>
        </div>

        {/* Recipients — a simple table of name + designation. */}
        <div className="space-y-1.5">
          <p className="status-label text-muted-foreground">{dv.meetings.sentRecipients}</p>
          {shown.length === 0 ? (
            <p className="text-sm text-muted-foreground">{dv.meetings.recipientsDefault}</p>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-[hsl(var(--border))]">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-[hsl(var(--border))]">
                  {shown.map((m) => (
                    <tr key={m.id}>
                      <td className="px-3 py-2 font-medium text-foreground/90">{displayName(m)}</td>
                      <td className="px-3 py-2 text-end text-muted-foreground">
                        {m.designationDv ?? dv.roles[m.role] ?? m.role}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    );
  }

  const summary = shown.length === 0
    ? <p className="text-sm text-muted-foreground">{dv.meetings.recipientsDefault}</p>
    : <ul className="flex flex-col gap-1 text-sm">{shown.map((m) => <li key={m.id}>{displayName(m)}</li>)}</ul>;

  if (!editable || !open) {
    return (
      <section className="rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6 space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="status-label text-muted-foreground">{dv.meetings.recipients}</h2>
          {editable && (
            <button
              className="btn-secondary btn-sm px-2"
              onClick={() => setOpen(true)}
              aria-label={dv.common.edit}
              title={dv.common.edit}
            >
              <Icon name="edit" size={15} />
            </button>
          )}
        </div>
        {summary}
      </section>
    );
  }

  const none = selected.length === 0;
  return (
    <section className="rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6 space-y-2">
      <h2 className="status-label text-muted-foreground">{dv.meetings.recipients}</h2>
      <div className="flex flex-col gap-2 rounded-2xl border border-[hsl(var(--border))] p-3">
        {!members ? (
          <span className="text-sm text-muted-foreground">{dv.common.loading}</span>
        ) : members.map((m) => (
          <label key={m.id} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={selected.includes(m.id)}
              onChange={() => setSelected((p) => p.includes(m.id) ? p.filter((x) => x !== m.id) : [...p, m.id])}
            />
            <span>{displayName(m)}</span>
            <span className="text-xs text-muted-foreground">{m.designationDv ?? dv.roles[m.role] ?? m.role}</span>
          </label>
        ))}
      </div>
      {none && <p className="text-sm text-destructive">{dv.meetings.recipientsRequired}</p>}
      <div className="flex gap-2">
        <button disabled={none || save.isPending} className="btn-primary btn-sm" onClick={() => save.mutate()}>{dv.common.save}</button>
        <button className="btn-ghost btn-sm" onClick={() => { setSelected(recipientIds); setOpen(false); }}>{dv.common.cancel}</button>
      </div>
      {save.isError && <p className="text-sm text-destructive">{dv.common.error}</p>}
    </section>
  );
}
